import { createContext, type ReactNode, useContext, useEffect, useReducer } from 'react';
import getSearchClient from '@/server/repository/algolia/client';
import transformAlgoliaRecipes from '@/server/repository/algolia/transformer/Recipes/AlgoliaRecipesTransformer';
import { RelatedDataService } from '@/service/relatedData/relatedDataService';
import type { PathTranslations } from '@/service/localization/getPathTranslations';
import type { ISuggestedData } from '@/components/Cards/types';
import type { FilterTopic } from '@/components/FilterMenu/types';
import { useRouter } from 'next/router';
import { RECIPE_SEARCH_SORT_METHODS } from '@/hooks/useSort';
import { getSortedIndexName } from '@/service/search/getSortedIndexName';

type Action =
  | { type: 'SET_RESET_STATUS'; isResetting: boolean }
  | { type: 'FETCH_RECIPES_START' }
  | {
      type: 'FETCH_RECIPES_SUCCESS';
      totalPages: number;
      totalResults: number;
      facets?: any;
    }
  | { type: 'FETCH_RECIPES_ERROR' }
  | { type: 'SET_FILTERS'; filters: FilterTopic[] }
  | { type: 'SET_PAGE'; page: number }
  | { type: 'SET_SORT_METHOD'; method: string }
  | { type: 'SET_FACET_FILTERS'; facetFilters: string[][] }
  | {
      type: 'SET_INDEX_CONFIG';
      indexConfig: { type: string; locale?: string; recipeEnv?: string };
    }
  | { type: 'SET_INDEX_NAME'; indexName: string }
  | {
      type: 'APPEND_RECIPES';
      recipes: ISuggestedData[];
    }
  | { type: 'RESET' }
  | { type: 'SET_RECIPES'; recipes: ISuggestedData[] };
type Dispatch = (action: Action) => void;
type State = {
  recipes: ISuggestedData[];
  loading: boolean;
  isResetting: boolean;
  error: boolean;
  totalPages: number;
  page: number;
  totalResults: number;
  sortMethod: string;
  filters?: FilterTopic[];
  facetFilters?: string[][];
  facets?: any;
  indexName?: string;
  indexConfig: {
    type: string;
    locale?: string;
    recipeEnv?: string;
  };
};
type FetchRecipeOptions = {
  dispatch: Dispatch;
  facets?: any;
  indexName: string;
  page?: number;
  pageSize?: number;
  filters?: string;
  facetFilters?: string[][];
  pathTranslations: PathTranslations;
  searchPageLabel: string;
  query?: string;
};

const fetchRecipes = async (options: FetchRecipeOptions) => {
  const {
    dispatch,
    indexName,
    page,
    pageSize,
    facets,
    facetFilters,
    filters,
    searchPageLabel,
    pathTranslations,
    query = '',
  } = options;
  const client = getSearchClient().initIndex(indexName);

  dispatch({ type: 'FETCH_RECIPES_START' });
  try {
    const response = await client.search(query, {
      filters: filters ?? '',
      facetFilters: facetFilters ?? [],
      page: page ?? 0,
      hitsPerPage: pageSize ?? 6,
      facets: facets ?? [],
      analyticsTags: ['recipe_category_page'],
      clickAnalytics: true,
    });

    const transformedRecipes = transformAlgoliaRecipes(response.hits).filter(
      (item) => !item.Id || !item.slug,
    );
    const parsedRecipes = RelatedDataService.getRelatedData(
      transformedRecipes,
      pathTranslations,
      'recipe',
      searchPageLabel,
    );

    dispatch({
      type: 'FETCH_RECIPES_SUCCESS',
      totalPages: response.nbPages,
      totalResults: response.nbHits,
      facets: response.facets,
    });

    dispatch({
      type: 'APPEND_RECIPES',
      recipes: parsedRecipes,
    });
  } catch (error) {
    dispatch({ type: 'FETCH_RECIPES_ERROR' });
  }
};

// Sets the faced filters
// This are filters that can be selected in the filter menu, e.g.
// facetFilters":[["occasions:Geburtstag"]]
const applyFilters = (filters: FilterTopic[], dispatch: Dispatch) => {
  const facetFilters = filters
    .map((filter) => filter.options.filter((option) => option.selected).map((option) => option.key))
    .filter((current) => current.length);

  // When we apply filters, we reset the page to 0 and the recipes
  // Applying new filters triggers a new search request. So we reset the page and recipes to make sure no old data is shown
  dispatch({ type: 'SET_FACET_FILTERS', facetFilters });
  dispatch({ type: 'SET_PAGE', page: 0 });
  dispatch({ type: 'SET_RECIPES', recipes: [] });
};

// Updates the filters that are shown in the filter menu
const updateFilters = (
  facets: Record<string, Record<string, number>>,
  searchItemLabels: Record<string, string>,
  appliedFilters: string[][],
  dispatch: Dispatch,
) => {
  const difficultLabels: Record<string, string> = {
    0: searchItemLabels?.easy || 'Easy',
    1: searchItemLabels?.intermediate || 'Intermediate',
    2: searchItemLabels?.tough || 'Tough',
  };
  const updatedFilters = Object.keys(facets).map((key: string) => ({
    key,
    label: searchItemLabels?.[`filter_${key}`] || key,
    options: Object.keys(facets[key]).map((opt: string) => ({
      key: `${key}:${opt}`,
      label:
        key === 'difficulty' ? difficultLabels[opt] : searchItemLabels?.[`${key}:${opt}`] || opt,
      selected: appliedFilters?.some(
        (appliedFilter) => appliedFilter.indexOf(`${key}:${opt}`) > -1,
      ),
      itemCount: facets[key][opt],
    })),
    type: key === 'rating_effective' ? 'rating' : 'text',
  })) as FilterTopic[];

  if (updatedFilters.length) {
    dispatch({ type: 'SET_FILTERS', filters: updatedFilters });
  }
};

const RecipeGridContext = createContext<{ state: State; dispatch: Dispatch } | undefined>(
  undefined,
);

const recipeGridReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'SET_RESET_STATUS': {
      return { ...state, isResetting: action.isResetting };
    }
    case 'FETCH_RECIPES_START': {
      return { ...state, loading: true, error: false };
    }
    case 'FETCH_RECIPES_SUCCESS': {
      return {
        ...state,
        loading: false,
        error: false,
        totalPages: action.totalPages,
        totalResults: action.totalResults,
        facets: action.facets,
      };
    }
    case 'APPEND_RECIPES': {
      // Prevent duplicate recipes
      const uniqueRecipes = action.recipes.filter(
        (recipe) => !state.recipes.some((r) => r.id === recipe.id),
      );

      return {
        ...state,
        recipes: [...state.recipes, ...uniqueRecipes],
      };
    }
    case 'FETCH_RECIPES_ERROR': {
      return { ...state, loading: false, error: true };
    }
    case 'SET_PAGE': {
      return { ...state, page: action.page };
    }
    case 'SET_FILTERS': {
      return { ...state, filters: action.filters };
    }
    case 'SET_SORT_METHOD': {
      return { ...state, sortMethod: action.method };
    }
    case 'SET_FACET_FILTERS': {
      return { ...state, facetFilters: action.facetFilters };
    }
    case 'SET_INDEX_CONFIG': {
      return { ...state, indexConfig: action.indexConfig };
    }
    case 'SET_INDEX_NAME': {
      return { ...state, indexName: action.indexName };
    }
    case 'RESET': {
      return {
        ...state,
        recipes: [],
        loading: false,
        isResetting: true,
        error: false,
        totalPages: 0,
        page: 0,
        totalResults: 0,
        sortMethod: RECIPE_SEARCH_SORT_METHODS.RELEVENCE,
        filters: undefined,
        appliedFilters: undefined,
        facetFilters: undefined,
      };
    }
    case 'SET_RECIPES': {
      return { ...state, recipes: action.recipes };
    }
    default: {
      throw new Error('Unhandled action type');
    }
  }
};

const RecipeGridProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(recipeGridReducer, {
    recipes: [],
    loading: false,
    isResetting: false,
    error: false,
    totalPages: 0,
    page: 0,
    totalResults: 0,
    sortMethod: RECIPE_SEARCH_SORT_METHODS.RELEVENCE,
    facetFilters: undefined,
    filters: undefined,
    indexConfig: {
      type: 'RECIPES',
    },
  });

  const router = useRouter();

  // Reset filters and search results when page changes
  useEffect(() => {
    dispatch({ type: 'RESET' });
  }, [router.asPath]);

  // Check if reset is done
  useEffect(() => {
    if (state.isResetting && state.recipes.length === 0) {
      dispatch({ type: 'SET_RESET_STATUS', isResetting: false });
    }
  }, [state.isResetting, state.recipes]);

  // Update index name when index config changes or sort method changes
  useEffect(() => {
    if (state.indexConfig.type && state.indexConfig.recipeEnv && state.indexConfig.locale) {
      const indexName = getSortedIndexName(
        state.indexConfig.type,
        state.indexConfig.recipeEnv,
        state.indexConfig.locale,
        state.sortMethod,
      );
      dispatch({ type: 'SET_RECIPES', recipes: [] });
      dispatch({ type: 'SET_INDEX_NAME', indexName });
    }
  }, [state.indexConfig, state.sortMethod]);

  const value = { state, dispatch };

  return <RecipeGridContext.Provider value={value}>{children}</RecipeGridContext.Provider>;
};

const useRecipeGrid = () => {
  const context = useContext(RecipeGridContext);
  if (context === undefined) {
    throw new Error('useRecipeGrid must be used within a RecipeGridProvider');
  }
  return context;
};

export { RecipeGridProvider, useRecipeGrid, fetchRecipes, applyFilters, updateFilters };
